import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { paymentsApi } from '@/services/payments'
import { invalidateNotificationQueries } from '@/hooks/useNotifications'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import type {
  PaymentListQuery,
  CreatePaymentRequest,
  UpdatePaymentStatusRequest,
} from '@/types'

type UpdatePaymentStatusOptions = {
  jobId?: string | null
  invoiceId?: string | null
}

function invalidatePaymentStatusCaches(
  qc: QueryClient,
  paymentId: string,
  jobId?: string | null,
  invoiceId?: string | null
) {
  const invalidations = [
    qc.invalidateQueries({ queryKey: ['payments', 'list'] }),
    qc.invalidateQueries({ queryKey: ['payments', 'summary'] }),
    qc.invalidateQueries({ queryKey: ['payments', 'detail', paymentId] }),
    qc.invalidateQueries({ queryKey: ['invoices', 'list'] }),
    qc.invalidateQueries({ queryKey: ['invoices', 'summary'] }),
    invalidatePortalMetricQueries(qc),
    invalidateNotificationQueries(qc),
  ]

  if (invoiceId) {
    invalidations.push(
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoiceId] })
    )
  }

  if (jobId) {
    invalidations.push(
      qc.invalidateQueries({ queryKey: ['jobs', 'detail', jobId] }),
      qc.invalidateQueries({ queryKey: ['jobs'] }),
      qc.invalidateQueries({ queryKey: ['agenda'] })
    )
  }

  return Promise.all(invalidations)
}

export function paymentsListQueryOptions(q: PaymentListQuery, staleTime = 0) {
  return queryOptions({
    queryKey: ['payments', 'list', q],
    queryFn: () => paymentsApi.list(q),
    staleTime,
  })
}

export function usePaymentsList(
  q: PaymentListQuery,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery({
    ...paymentsListQueryOptions(q, options?.staleTime),
    enabled: options?.enabled ?? true,
  })
}

// Tenant-wide summary card counts. KPI clicks filter the list only; the summary
// stays stable so card values do not collapse to the selected bucket.
export function usePaymentsSummary() {
  return useQuery({
    queryKey: ['payments', 'summary'],
    queryFn: () => paymentsApi.summary(),
  })
}

export function usePayment(id: string | undefined) {
  return useQuery({
    queryKey: ['payments', 'detail', id],
    queryFn: () => paymentsApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreatePayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePaymentRequest) => paymentsApi.create(body),
    onSuccess: (_res, body) => {
      qc.invalidateQueries({ queryKey: ['payments', 'list'] })
      qc.invalidateQueries({ queryKey: ['payments', 'summary'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
      if (body.job_id) {
        qc.invalidateQueries({ queryKey: ['jobs', 'detail', body.job_id] })
        qc.invalidateQueries({ queryKey: ['jobs'] })
        qc.invalidateQueries({ queryKey: ['agenda'] })
      }
    },
  })
}

export function useUpdatePaymentStatus(
  id: string,
  options: UpdatePaymentStatusOptions = {}
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdatePaymentStatusRequest) =>
      paymentsApi.updateStatus(id, body),
    onSuccess: async res => {
      const jobId = options.jobId ?? res.data?.job_id
      const invoiceId = options.invoiceId ?? res.data?.invoice_id
      await invalidatePaymentStatusCaches(qc, id, jobId, invoiceId)
    },
  })
}
